import { create } from 'zustand';
import type {
  Season,
  Week,
  UserProfile,
  Exercise,
  WorkoutDay,
  Session,
} from '../types/workout';
import {
  isRestDay,
  walkSeasonWeeks,
} from '../types/workout';
import { loadSeason, saveSeason, extendSeason as extendSeasonApi } from '../lib/api';
import { deepClone } from '../lib/utils';
import { toast } from 'sonner';

function findWeekForDate(season: Season, date: string): Week | null {
  for (const cycle of season.cycles) {
    for (const week of cycle.weeks) {
      if (date in week.days) return week;
    }
  }
  return null;
}

/**
 * Ensure the day has a `sessions` array we can mutate. If the day was sent in
 * the legacy single-session shape (top-level exercises), promote it. Mutates
 * `day` in place and returns the sessions array.
 */
function ensureSessions(day: WorkoutDay): Session[] {
  if (Array.isArray(day.sessions) && day.sessions.length > 0) {
    return day.sessions;
  }
  const session: Session = {
    category: day.category,
    name: day.name,
    goal: day.goal,
    priorities: day.priorities,
    reason: day.reason,
    exercises: Array.isArray(day.exercises) ? day.exercises : [],
  };
  day.sessions = [session];
  return day.sessions;
}

interface WorkoutState {
  currentUser: UserProfile | null;
  season: Season | null;
  // Snapshot of the last server-confirmed state. Used as the baseline that
  // `discardChanges` reverts to and that `isDirty` compares against.
  savedSeason: Season | null;
  // True once a local mutation has diverged the working `season` from
  // `savedSeason`. Drives the Save / Refresh button enablement.
  isDirty: boolean;
  isLoading: boolean;
  error: string | null;
  isSaving: boolean;
  isExtending: boolean;
  currentWeekIndex: number;

  setCurrentUser: (user: UserProfile | null) => void;
  loadUserSeason: (userId: string) => Promise<void>;
  setSeason: (season: Season | null) => void;

  // Exercise CRUD (session-aware) — local-only; commit via saveChanges()
  updateExercise: (
    date: string,
    sessionIndex: number,
    exerciseIndex: number,
    updates: Partial<Exercise>
  ) => void;
  addExercise: (date: string, sessionIndex: number, exercise: Exercise) => void;
  deleteExercise: (date: string, sessionIndex: number, exerciseIndex: number) => void;
  updateWorkoutGoal: (date: string, goal: string) => void;

  swapDays: (date1: string, date2: string) => void;
  /**
   * Move the day at `fromDate` to `toDate`. If `toDate` already has content,
   * the two days swap. If `toDate` is empty (no key in any week's days), the
   * source day moves and `fromDate` becomes a placeholder rest day so the
   * week's date set stays consistent.
   */
  moveDayToDate: (fromDate: string, toDate: string) => void;
  swapSessions: (date: string, fromIndex: number, toIndex: number) => void;
  /** Move a whole session from one day to another, preserving its content
   *  (category, name, goal, exercises). Appends to the target day; a rest day
   *  or empty slot is promoted into a workout day carrying the moved session. */
  moveSessionToDay: (fromDate: string, fromIndex: number, toDate: string) => void;
  /** Append a new empty session to the day, optionally tagged with the
   *  given training modality (Weightlifting, Run, Yoga, …). */
  addSession: (date: string, category?: string) => void;
  /** Remove the session at the given index. If it was the last session,
   *  the day stays as a workout day with zero sessions (admin can re-add
   *  one or convert to rest day explicitly). */
  deleteSession: (date: string, sessionIndex: number) => void;
  /** Re-tag a session's training modality (sets `category` and updates
   *  the legacy `name` so DayCard's label stays in sync). */
  updateSessionCategory: (
    date: string,
    sessionIndex: number,
    category: string,
  ) => void;
  /** Set the day-level title (the "Strength Training" subtitle under the
   *  date). Writes `day.category` — the field getDayLabel reads first. */
  updateDayCategory: (date: string, category: string) => void;
  /** Set a per-session title (e.g. "Full Body"). Free text. */
  updateSessionName: (date: string, sessionIndex: number, name: string) => void;
  /** Set a per-session free-text goal — repurposed by admins as the
   *  cardio "workout structure" field (e.g. "5x400m at threshold w/ 1min rest"). */
  updateSessionGoal: (date: string, sessionIndex: number, goal: string) => void;
  convertToRestDay: (date: string) => void;
  convertToWorkoutDay: (date: string) => void;

  /** Push the current in-memory `season` to the backend. On success the
   *  saved baseline + dirty flag are reset. */
  saveChanges: () => Promise<void>;
  discardChanges: () => void;

  extendSeason: () => Promise<void>;

  // Week navigation (multi-cycle aware: weeks are flattened across all cycles)
  setCurrentWeekIndex: (index: number) => void;
  nextWeek: () => void;
  previousWeek: () => void;

  reset: () => void;
}

// Mutate the working season in place via `mutate`, then commit the new
// reference + flip `isDirty`. Used by every editor action so they share
// identical clone / dirty-flag wiring.
function applyLocalEdit(
  get: () => WorkoutState,
  set: (partial: Partial<WorkoutState>) => void,
  mutate: (season: Season) => boolean | void,
): void {
  const { season } = get();
  if (!season) return;
  const updated = deepClone(season);
  const result = mutate(updated);
  if (result === false) return;
  set({ season: updated, isDirty: true });
}

export const useWorkoutStore = create<WorkoutState>((set, get) => ({
  currentUser: null,
  season: null,
  savedSeason: null,
  isDirty: false,
  isLoading: false,
  error: null,
  isSaving: false,
  isExtending: false,
  currentWeekIndex: 0,

  setCurrentUser: (user) => set({ currentUser: user }),

  loadUserSeason: async (userId: string) => {
    set({ isLoading: true, error: null });
    try {
      const season = await loadSeason(userId);
      set({
        season,
        savedSeason: season ? deepClone(season) : null,
        isDirty: false,
        isLoading: false,
        currentWeekIndex: 0,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load season';
      set({ error: message, isLoading: false });
      toast.error(message);
    }
  },

  // Set season directly (used by chat-edit flows that bypass the editor)
  setSeason: (season) =>
    set({
      season,
      savedSeason: season ? deepClone(season) : null,
      isDirty: false,
    }),

  updateExercise: (date, sessionIndex, exerciseIndex, updates) => {
    applyLocalEdit(get, set, (season) => {
      const week = findWeekForDate(season, date);
      if (!week) return false;

      const day = week.days[date];
      if (isRestDay(day)) return false;

      const sessions = ensureSessions(day as WorkoutDay);
      const session = sessions[sessionIndex];
      if (!session || !Array.isArray(session.exercises)) return false;
      const exercise = session.exercises[exerciseIndex];
      if (!exercise) return false;

      Object.assign(exercise, updates);

      if (sessions.length === 1) {
        (day as WorkoutDay).exercises = sessions[0].exercises;
      }
    });
  },

  addExercise: (date, sessionIndex, exercise) => {
    applyLocalEdit(get, set, (season) => {
      const week = findWeekForDate(season, date);
      if (!week) return false;

      const day = week.days[date];
      if (isRestDay(day)) return false;

      const sessions = ensureSessions(day as WorkoutDay);
      const session = sessions[sessionIndex] ?? sessions[0];
      if (!session) return false;
      if (!Array.isArray(session.exercises)) session.exercises = [];
      session.exercises.push(exercise);

      if (sessions.length === 1) {
        (day as WorkoutDay).exercises = sessions[0].exercises;
      }
    });
  },

  deleteExercise: (date, sessionIndex, exerciseIndex) => {
    applyLocalEdit(get, set, (season) => {
      const week = findWeekForDate(season, date);
      if (!week) return false;

      const day = week.days[date];
      if (isRestDay(day)) return false;

      const sessions = ensureSessions(day as WorkoutDay);
      const session = sessions[sessionIndex];
      if (!session || !Array.isArray(session.exercises)) return false;
      session.exercises.splice(exerciseIndex, 1);

      if (sessions.length === 1) {
        (day as WorkoutDay).exercises = sessions[0].exercises;
      }
    });
  },

  // Update day-level goal (kept on legacy field; new shape may also surface
  // it via the first session, but we don't overwrite session goals).
  updateWorkoutGoal: (date, goal) => {
    applyLocalEdit(get, set, (season) => {
      const week = findWeekForDate(season, date);
      if (!week) return false;

      const day = week.days[date];
      if (isRestDay(day)) return false;

      (day as WorkoutDay).goal = goal;
    });
  },

  swapDays: (date1, date2) => {
    applyLocalEdit(get, set, (season) => {
      const week1 = findWeekForDate(season, date1);
      const week2 = findWeekForDate(season, date2);
      if (!week1 || !week2) return false;

      const temp = week1.days[date1];
      week1.days[date1] = week2.days[date2];
      week2.days[date2] = temp;
    });
  },

  // Move the workout from one date to another (cross-week capable). The
  // destination must already exist in the season — both call sites (week
  // chip drop, "Move to date" submenu) only surface existing dates, so a
  // missing target indicates a stale UI state we want to refuse.
  // When dates land on different weekdays we recompute `day_name` so labels
  // stay consistent with the calendar slot.
  moveDayToDate: (fromDate, toDate) => {
    if (fromDate === toDate) return;
    applyLocalEdit(get, set, (season) => {
      const fromWeek = findWeekForDate(season, fromDate);
      const toWeek = findWeekForDate(season, toDate);
      if (!fromWeek || !toWeek) {
        toast.error('Cannot move day: destination is no longer in the season');
        return false;
      }

      const dayNameOf = (iso: string): string =>
        ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][
          new Date(iso + 'T12:00:00').getDay()
        ];

      const sourceDay = fromWeek.days[fromDate];
      const targetDay = toWeek.days[toDate];

      const relabel = (day: typeof sourceDay, date: string): typeof sourceDay => {
        if (!day) return day;
        if ('day_name' in day) {
          return { ...day, day_name: dayNameOf(date) };
        }
        return day;
      };

      toWeek.days[toDate] = relabel(sourceDay, toDate);
      fromWeek.days[fromDate] = relabel(targetDay, fromDate);
    });
  },

  // No-op if either index is invalid.
  swapSessions: (date, fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    applyLocalEdit(get, set, (season) => {
      const week = findWeekForDate(season, date);
      if (!week) return false;

      const day = week.days[date];
      if (isRestDay(day)) return false;

      const sessions = ensureSessions(day as WorkoutDay);
      if (fromIndex < 0 || fromIndex >= sessions.length) return false;
      if (toIndex < 0 || toIndex >= sessions.length) return false;

      const [moved] = sessions.splice(fromIndex, 1);
      sessions.splice(toIndex, 0, moved);

      if (sessions.length === 1) {
        (day as WorkoutDay).exercises = sessions[0].exercises;
      }
    });
  },

  // Move a whole session across days, preserving its content. Splice it out
  // of the source day, append it to the target. A rest day or empty target
  // slot is promoted into a workout day carrying the moved session as its
  // sole entry. Cross-week capable (findWeekForDate walks every cycle). The
  // legacy single-session `exercises` mirror is kept in sync on both ends so
  // one-session days still round-trip through the legacy shape.
  moveSessionToDay: (fromDate, fromIndex, toDate) => {
    if (fromDate === toDate) return;
    applyLocalEdit(get, set, (season) => {
      const fromWeek = findWeekForDate(season, fromDate);
      const toWeek = findWeekForDate(season, toDate);
      if (!fromWeek || !toWeek) return false;

      const sourceDay = fromWeek.days[fromDate];
      if (isRestDay(sourceDay)) return false;

      const sourceSessions = ensureSessions(sourceDay as WorkoutDay);
      if (fromIndex < 0 || fromIndex >= sourceSessions.length) return false;
      const [moved] = sourceSessions.splice(fromIndex, 1);

      // Re-sync the source's legacy mirror: 1 session left -> mirror it;
      // 0 or 2+ -> clear it.
      if (sourceSessions.length === 1) {
        (sourceDay as WorkoutDay).exercises = sourceSessions[0].exercises;
      } else {
        (sourceDay as WorkoutDay).exercises = undefined;
      }

      const targetDay = toWeek.days[toDate];
      const day_name =
        targetDay && 'day_name' in targetDay ? targetDay.day_name : undefined;

      if (!targetDay || isRestDay(targetDay)) {
        // Promote a rest day / empty slot into a workout day carrying the
        // moved session. Mirror the legacy single-session shape.
        toWeek.days[toDate] = {
          category: moved.category,
          name: moved.name,
          goal: moved.goal,
          priorities: [],
          reason: '',
          exercises: Array.isArray(moved.exercises) ? moved.exercises : [],
          sessions: [moved],
          ...(day_name && { day_name }),
        };
        return;
      }

      const targetSessions = ensureSessions(targetDay as WorkoutDay);
      targetSessions.push(moved);

      // Re-sync the target's legacy mirror (2+ now -> clear; exactly 1 is
      // impossible here because we just pushed onto an existing day).
      if (targetSessions.length > 1) {
        (targetDay as WorkoutDay).exercises = undefined;
      } else {
        (targetDay as WorkoutDay).exercises = targetSessions[0].exercises;
      }
    });
  },

  // Append a new (empty) session to a day. Defaults to "Strength Training"
  // when no modality is supplied — admin can change it via the per-session
  // category dropdown immediately after.
  addSession: (date, category) => {
    applyLocalEdit(get, set, (season) => {
      const week = findWeekForDate(season, date);
      if (!week) return false;
      const day = week.days[date];
      if (isRestDay(day)) return false;

      const cat = category && category.trim() ? category.trim() : 'Strength Training';
      const sessions = ensureSessions(day as WorkoutDay);
      sessions.push({
        category: cat,
        name: cat,
        goal: '',
        exercises: [],
      });

      if (sessions.length > 1) {
        (day as WorkoutDay).exercises = undefined;
      }
    });
  },

  // Remove a session from a day. Leaves the day in workout-day shape even
  // when zero sessions remain (admin can re-add or convert to rest day).
  deleteSession: (date, sessionIndex) => {
    applyLocalEdit(get, set, (season) => {
      const week = findWeekForDate(season, date);
      if (!week) return false;
      const day = week.days[date];
      if (isRestDay(day)) return false;

      const sessions = ensureSessions(day as WorkoutDay);
      if (sessionIndex < 0 || sessionIndex >= sessions.length) return false;
      sessions.splice(sessionIndex, 1);

      if (sessions.length === 1) {
        (day as WorkoutDay).exercises = sessions[0].exercises;
      } else {
        (day as WorkoutDay).exercises = undefined;
      }
    });
  },

  // Re-tag the training modality of a session. We update both `category`
  // (the canonical field the agent / backend reads) and `name` IF the
  // current name was either empty or matched the old category — that
  // preserves any custom session name the admin or agent set.
  updateSessionCategory: (date, sessionIndex, category) => {
    const trimmed = category.trim();
    if (!trimmed) return;
    applyLocalEdit(get, set, (season) => {
      const week = findWeekForDate(season, date);
      if (!week) return false;
      const day = week.days[date];
      if (isRestDay(day)) return false;

      const sessions = ensureSessions(day as WorkoutDay);
      const session = sessions[sessionIndex];
      if (!session) return false;

      const previousCategory = session.category;
      const previousName = session.name;
      session.category = trimmed;
      if (!previousName || previousName === previousCategory) {
        session.name = trimmed;
      }
    });
  },

  // Set the day-level title — i.e. `day.category`, the field DayCard's
  // getDayLabel reads first. Accepted as free text; the UI offers a
  // dropdown of the agent's enum but admins can type custom strings.
  updateDayCategory: (date, category) => {
    applyLocalEdit(get, set, (season) => {
      const week = findWeekForDate(season, date);
      if (!week) return false;
      const day = week.days[date];
      if (isRestDay(day)) return false;

      (day as WorkoutDay).category = category.trim();
    });
  },

  // Set a per-session title (free text, e.g. "Full Body"). Mirrors to
  // the legacy top-level `day.name` when there's exactly one session.
  updateSessionName: (date, sessionIndex, name) => {
    applyLocalEdit(get, set, (season) => {
      const week = findWeekForDate(season, date);
      if (!week) return false;
      const day = week.days[date];
      if (isRestDay(day)) return false;

      const sessions = ensureSessions(day as WorkoutDay);
      const session = sessions[sessionIndex];
      if (!session) return false;

      session.name = name;
      if (sessions.length === 1) {
        (day as WorkoutDay).name = name;
      }
    });
  },

  // Set a per-session free-text goal. Admins use this as the cardio
  // "workout structure" field (e.g. "5x400m at threshold, 1min rest").
  // Mirrors to the legacy top-level `day.goal` when there's exactly one
  // session — keeps the day-level description and the lone session's
  // structure coherent in the legacy shape.
  updateSessionGoal: (date, sessionIndex, goal) => {
    applyLocalEdit(get, set, (season) => {
      const week = findWeekForDate(season, date);
      if (!week) return false;
      const day = week.days[date];
      if (isRestDay(day)) return false;

      const sessions = ensureSessions(day as WorkoutDay);
      const session = sessions[sessionIndex];
      if (!session) return false;

      session.goal = goal;
      if (sessions.length === 1) {
        (day as WorkoutDay).goal = goal;
      }
    });
  },

  convertToRestDay: (date) => {
    applyLocalEdit(get, set, (season) => {
      const week = findWeekForDate(season, date);
      if (!week) return false;

      const currentDay = week.days[date];
      const day_name = 'day_name' in currentDay ? currentDay.day_name : undefined;
      week.days[date] = {
        rest_day: true,
        category: 'Rest',
        sessions: [{ rest_day: true, category: 'Rest' }],
        ...(day_name && { day_name }),
      };
    });
  },

  convertToWorkoutDay: (date) => {
    applyLocalEdit(get, set, (season) => {
      const week = findWeekForDate(season, date);
      if (!week) return false;

      const currentDay = week.days[date];
      const day_name = 'day_name' in currentDay ? currentDay.day_name : undefined;
      week.days[date] = {
        name: 'New Workout',
        category: 'Workout',
        goal: '',
        priorities: [],
        reason: '',
        exercises: [],
        sessions: [{ category: 'Workout', name: 'New Workout', goal: '', exercises: [] }],
        ...(day_name && { day_name }),
      };
    });
  },

  // Commit the in-memory season to the backend. Refreshes the saved
  // baseline + clears the dirty flag on success; leaves the working state
  // untouched on failure so the admin can retry.
  saveChanges: async () => {
    const { season, currentUser, isSaving } = get();
    if (!season || !currentUser || isSaving) return;

    set({ isSaving: true });
    try {
      await saveSeason(currentUser.id, season);
      set({
        savedSeason: deepClone(season),
        isDirty: false,
      });
      toast.success('Workouts saved');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save';
      toast.error(message);
    } finally {
      set({ isSaving: false });
    }
  },

  // No-op when there are no edits.
  discardChanges: () => {
    const { savedSeason, isDirty } = get();
    if (!isDirty || !savedSeason) return;
    set({
      season: deepClone(savedSeason),
      isDirty: false,
    });
    toast.success('Reverted to last saved workouts');
  },

  extendSeason: async () => {
    const { currentUser, season } = get();
    if (!currentUser) return;

    set({ isExtending: true });

    try {
      const updatedSeason = await extendSeasonApi(currentUser.id);
      // Advance the calendar to the newly added week (the last week across all cycles).
      const flattened = walkSeasonWeeks(updatedSeason);
      const previousCount = season ? walkSeasonWeeks(season).length : 0;
      const targetIndex = Math.max(0, Math.min(flattened.length - 1, previousCount));
      set({
        season: updatedSeason,
        savedSeason: deepClone(updatedSeason),
        isDirty: false,
        currentWeekIndex: targetIndex,
      });
      toast.success('Plan extended with next week');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to extend season';
      toast.error(message);
    } finally {
      set({ isExtending: false });
    }
  },

  setCurrentWeekIndex: (index) => set({ currentWeekIndex: index }),

  nextWeek: () => {
    const { season, currentWeekIndex } = get();
    if (!season) return;
    const total = walkSeasonWeeks(season).length;
    if (currentWeekIndex < total - 1) {
      set({ currentWeekIndex: currentWeekIndex + 1 });
    }
  },

  previousWeek: () => {
    const { currentWeekIndex } = get();
    if (currentWeekIndex > 0) {
      set({ currentWeekIndex: currentWeekIndex - 1 });
    }
  },

  reset: () =>
    set({
      currentUser: null,
      season: null,
      savedSeason: null,
      isDirty: false,
      isLoading: false,
      error: null,
      isSaving: false,
      isExtending: false,
      currentWeekIndex: 0,
    }),
}));
